/**
 * SCM — BOM PDF/text extractor (four-tier, mirrors extractQuoteFromPdf).
 *
 * Tier 0 — structured CSV exports (Phase/Category/Item Name/Quantity/…)
 * Tier 1 — pipe/tab-delimited table rows and numbered line items
 * Tier 2 — section routing under BOM / Equipment Schedule headings
 * Tier 3 — optional LLM fallback for remaining rows
 */
import type { FieldProvenance } from '../../types/financialAssumptions';
import type { BomUnit, SCMCategory } from '../../types/supplyChain';
import { SCM_CATEGORIES } from '../../types/supplyChain';

export interface ExtractedBomLine {
  name: string;
  spec?: string;
  category?: SCMCategory;
  quantity?: number;
  unit?: BomUnit;
  unitCostUsd?: number | null;
  leadTimeMonthsLow?: number;
  leadTimeMonthsHigh?: number;
}

export type ExtractedBomLineField = keyof ExtractedBomLine;

export interface BomExtractionSummary {
  lineCount: number;
  highConfidenceLines: number;
  mediumConfidenceLines: number;
  lowConfidenceLines: number;
}

export interface BomExtractionResult {
  lines: ExtractedBomLine[];
  lineProvenance: Partial<Record<number, Partial<Record<ExtractedBomLineField, FieldProvenance>>>>;
  warnings: string[];
  summary: BomExtractionSummary;
}

export type BomLlmExtractor = (
  text: string,
) => Promise<{ lines: ExtractedBomLine[] }>;

export interface ExtractBomOptions {
  llmExtractor?: BomLlmExtractor;
  now?: () => string;
}

const CATEGORY_KEYWORDS: { pattern: RegExp; category: SCMCategory }[] = [
  { pattern: /\b(?:smr|reactor|turbine|generator|solar|wind|inverter|module)\b/i, category: 'power-generation' },
  { pattern: /\b(?:transformer|switchgear|gsu|hvdc|substation)\b/i, category: 'power-conversion' },
  { pattern: /\b(?:bess|battery|megapack|storage)\b/i, category: 'energy-storage' },
  { pattern: /\b(?:cooling|chiller|crah|tower|dlc)\b/i, category: 'cooling' },
  { pattern: /\b(?:rack|ups|pdu|server|cabling)\b/i, category: 'data-center-it' },
  { pattern: /\b(?:fiber|connectivity|ix)\b/i, category: 'connectivity' },
  { pattern: /\b(?:concrete|steel|foundation|civil)\b/i, category: 'civil-construction' },
  { pattern: /\b(?:haul|logistics|rail|port|permit)\b/i, category: 'logistics' },
  { pattern: /\b(?:commission|nde|startup|inspection)\b/i, category: 'commissioning' },
];

const UNIT_MAP: Record<string, BomUnit> = {
  ea: 'each', each: 'each', lot: 'lot', mw: 'MW', mwh: 'MWh', kva: 'kVA',
  ton: 'ton', tons: 'ton', m3: 'm3', m: 'm',
};

type CsvColumn =
  | 'name'
  | 'spec'
  | 'category'
  | 'quantity'
  | 'unit'
  | 'unitCostUsd'
  | 'leadTimeMonthsLow'
  | 'leadTimeMonthsHigh';

const CSV_HEADER_ALIASES: Record<string, CsvColumn> = {
  'item name': 'name',
  item: 'name',
  name: 'name',
  description: 'name',
  specification: 'spec',
  spec: 'spec',
  category: 'category',
  quantity: 'quantity',
  qty: 'quantity',
  unit: 'unit',
  uom: 'unit',
  'unit cost usd': 'unitCostUsd',
  'unit cost': 'unitCostUsd',
  'unit price': 'unitCostUsd',
  'lead time low (months)': 'leadTimeMonthsLow',
  'lead time low': 'leadTimeMonthsLow',
  'lead time high (months)': 'leadTimeMonthsHigh',
  'lead time high': 'leadTimeMonthsHigh',
};

function normalizeCsvHeader(raw: string): string {
  return raw.toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Parse one CSV row, respecting double-quoted fields with embedded commas. */
function parseCsvRow(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

function parseCsvNumber(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!cleaned) return undefined;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeCsvCategory(raw: string | undefined): SCMCategory | undefined {
  if (!raw) return undefined;
  const key = raw.trim().toLowerCase();
  return (SCM_CATEGORIES as readonly string[]).includes(key) ? (key as SCMCategory) : undefined;
}

function looksLikeBomCsv(text: string): boolean {
  const firstLine = text.split(/\r?\n/).find((l) => l.trim().length > 0)?.trim() ?? '';
  if (!firstLine.includes(',')) return false;
  const headers = parseCsvRow(firstLine).map(normalizeCsvHeader);
  const hasName = headers.some((h) => CSV_HEADER_ALIASES[h] === 'name');
  const hasQtyOrUnit = headers.some((h) => {
    const col = CSV_HEADER_ALIASES[h];
    return col === 'quantity' || col === 'unit';
  });
  return hasName && hasQtyOrUnit;
}

/** Tier 0 — structured CSV with Item Name / Quantity / Unit columns. */
function extractTierCsv(text: string, now: () => string): {
  lines: ExtractedBomLine[];
  provenance: BomExtractionResult['lineProvenance'];
} {
  const lines: ExtractedBomLine[] = [];
  const provenance: BomExtractionResult['lineProvenance'] = {};
  if (!looksLikeBomCsv(text)) return { lines, provenance };

  const rawLines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (rawLines.length < 2) return { lines, provenance };

  const headers = parseCsvRow(rawLines[0]!).map(normalizeCsvHeader);
  const colIndex: Partial<Record<CsvColumn, number>> = {};
  headers.forEach((h, i) => {
    const mapped = CSV_HEADER_ALIASES[h];
    if (mapped && colIndex[mapped] === undefined) colIndex[mapped] = i;
  });
  if (colIndex.name === undefined) return { lines, provenance };

  const get = (row: string[], col: CsvColumn): string | undefined => {
    const idx = colIndex[col];
    if (idx === undefined) return undefined;
    const val = row[idx]?.trim();
    return val || undefined;
  };

  for (let li = 1; li < rawLines.length; li++) {
    const cells = parseCsvRow(rawLines[li]!);
    const name = get(cells, 'name');
    if (!name || name.length < 4) continue;
    if (/^(description|item|qty|quantity|total|subtotal)$/i.test(name)) continue;

    const category =
      normalizeCsvCategory(get(cells, 'category')) ??
      inferCategory(name);
    const quantity = parseCsvNumber(get(cells, 'quantity')) ?? 1;
    const unitRaw = get(cells, 'unit')?.toLowerCase();
    const unit = unitRaw ? (UNIT_MAP[unitRaw] ?? 'each') : 'each';
    const unitCostUsd = parseMoney(get(cells, 'unitCostUsd'));
    const leadTimeMonthsLow = parseCsvNumber(get(cells, 'leadTimeMonthsLow'));
    const leadTimeMonthsHigh = parseCsvNumber(get(cells, 'leadTimeMonthsHigh'));

    const idx = lines.length;
    lines.push({
      name,
      ...(get(cells, 'spec') ? { spec: get(cells, 'spec') } : {}),
      ...(category ? { category } : {}),
      quantity,
      unit,
      unitCostUsd,
      ...(leadTimeMonthsLow !== undefined ? { leadTimeMonthsLow } : {}),
      ...(leadTimeMonthsHigh !== undefined ? { leadTimeMonthsHigh } : {}),
    });
    provenance[idx] = {
      name: { source: 'extracted-defined-pattern', confidence: 'high', extractedAt: now() },
    };
  }

  return { lines, provenance };
}

/** Tier 1 — numbered or pipe-separated rows: "1 | GSU transformer | 2 | ea | $1.2M" */
const NUMBERED_ROW =
  /^\s*(\d{1,4})[\.\)\|\t]\s*(.+?)(?:\s+(\d+(?:\.\d+)?)\s+(ea|each|lot|mw|mwh|kva|ton|m3|m)\b)?(?:\s+\$?\s*([0-9][0-9,]*(?:\.\d{1,2})?)\s*(?:k|m|b)?)?/gim;

const PIPE_ROW =
  /^\s*([^|\t]+)\|\s*([^|\t]+)(?:\|\s*(\d+(?:\.\d+)?))?(?:\|\s*(ea|each|lot|mw|mwh|kva|ton|m3|m))?(?:\|\s*\$?\s*([0-9][0-9,]*(?:\.\d{1,2})?))?/gim;

function inferCategory(text: string): SCMCategory | undefined {
  for (const { pattern, category } of CATEGORY_KEYWORDS) {
    if (pattern.test(text)) return category;
  }
  return undefined;
}

function parseMoney(raw: string | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[$,\s]/g, '').toLowerCase();
  const m = cleaned.match(/^([0-9]+(?:\.[0-9]+)?)(k|m|b)?$/);
  if (!m) return null;
  let n = parseFloat(m[1]!);
  if (m[2] === 'k') n *= 1_000;
  if (m[2] === 'm') n *= 1_000_000;
  if (m[2] === 'b') n *= 1_000_000_000;
  return Number.isFinite(n) ? n : null;
}

function extractTier1Rows(text: string, now: () => string): {
  lines: ExtractedBomLine[];
  provenance: BomExtractionResult['lineProvenance'];
} {
  const lines: ExtractedBomLine[] = [];
  const provenance: BomExtractionResult['lineProvenance'] = {};
  const seen = new Set<string>();

  const tryAdd = (nameRaw: string, qtyRaw?: string, unitRaw?: string, priceRaw?: string, source: FieldProvenance['source'] = 'extracted-defined-pattern') => {
    const name = nameRaw.replace(/\s+/g, ' ').trim();
    if (name.length < 4 || name.length > 200) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    if (/^(description|item|qty|quantity|total|subtotal)$/i.test(name)) return;
    seen.add(key);

    const line: ExtractedBomLine = {
      name,
      category: inferCategory(name),
      ...(qtyRaw ? { quantity: parseFloat(qtyRaw) } : { quantity: 1 }),
      ...(unitRaw ? { unit: UNIT_MAP[unitRaw.toLowerCase()] ?? 'each' } : { unit: 'each' }),
      unitCostUsd: parseMoney(priceRaw),
    };
    const idx = lines.length;
    lines.push(line);
    provenance[idx] = {
      name: { source, confidence: 'high', extractedAt: now() },
    };
  };

  let m: RegExpExecArray | null;
  NUMBERED_ROW.lastIndex = 0;
  while ((m = NUMBERED_ROW.exec(text)) !== null) {
    tryAdd(m[2]!, m[3], m[4], m[5]);
  }

  PIPE_ROW.lastIndex = 0;
  while ((m = PIPE_ROW.exec(text)) !== null) {
    tryAdd(m[1]!, m[3], m[4], m[5]);
  }

  return { lines, provenance };
}

/** Tier 2 — slice text under BOM heading and parse line-by-line. */
function extractTier2Section(text: string, now: () => string): ExtractedBomLine[] {
  const heading = /\b(?:bill\s+of\s+materials?|equipment\s+schedule|major\s+equipment)\b/i.exec(text);
  if (!heading) return [];
  const start = heading.index + heading[0].length;
  const section = text.slice(start, Math.min(text.length, start + 8000));
  const out: ExtractedBomLine[] = [];
  for (const rawLine of section.split('\n')) {
    const trimmed = rawLine.trim();
    if (trimmed.length < 6) continue;
    const qtyMatch = trimmed.match(/(\d+(?:\.\d+)?)\s+(ea|each|lot|mw|mwh|kva|ton|m3|m)\b/i);
    const priceMatch = trimmed.match(/\$\s*([0-9][0-9,]*(?:\.\d{1,2})?)\s*(k|m|b)?/i);
    const name = trimmed
      .replace(/^\d+[\.\)]\s*/, '')
      .replace(/\s+\d+(?:\.\d+)?\s+(?:ea|each|lot|mw|mwh|kva|ton|m3|m)\b.*$/i, '')
      .replace(/\$\s*[0-9][0-9,]*(?:\.\d{1,2})?\s*(?:k|m|b)?.*$/i, '')
      .trim();
    if (name.length < 4) continue;
    out.push({
      name,
      category: inferCategory(name),
      quantity: qtyMatch ? parseFloat(qtyMatch[1]!) : 1,
      unit: qtyMatch ? (UNIT_MAP[qtyMatch[2]!.toLowerCase()] ?? 'each') : 'each',
      unitCostUsd: priceMatch ? parseMoney(priceMatch[0]) : null,
    });
  }
  return out.slice(0, 200);
}

export async function extractBomFromPdf(
  text: string,
  options: ExtractBomOptions = {},
): Promise<BomExtractionResult> {
  const now = options.now ?? ((): string => new Date().toISOString());
  const warnings: string[] = [];

  const tierCsv = extractTierCsv(text, now);
  let lines = tierCsv.lines;
  let lineProvenance = tierCsv.provenance;

  if (lines.length < 2) {
    const tier1 = extractTier1Rows(text, now);
    if (tier1.lines.length > lines.length) {
      lines = tier1.lines;
      lineProvenance = tier1.provenance;
    }
  }

  if (lines.length < 2) {
    const tier2 = extractTier2Section(text, now);
    if (tier2.length > lines.length) {
      lines = tier2;
      lineProvenance = {};
      tier2.forEach((_, i) => {
        lineProvenance[i] = { name: { source: 'extracted-section', confidence: 'medium', extractedAt: now() } };
      });
    }
  }

  if (options.llmExtractor && lines.length < 2) {
    try {
      const llm = await options.llmExtractor(text);
      if (llm.lines.length > 0) {
        lines = llm.lines.map((l) => ({
          ...l,
          category: l.category && (SCM_CATEGORIES as readonly string[]).includes(l.category)
            ? l.category
            : inferCategory(l.name),
        }));
        lineProvenance = {};
        lines.forEach((_, i) => {
          lineProvenance[i] = { name: { source: 'extracted-llm', confidence: 'low', extractedAt: now() } };
        });
        warnings.push('Used LLM fallback for BOM line extraction — review carefully.');
      }
    } catch (err) {
      warnings.push(`LLM BOM extraction failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  if (lines.length === 0) {
    warnings.push('No BOM line items detected — try pasting a table or use Generate from intent.');
  }

  let high = 0;
  let med = 0;
  let low = 0;
  for (let i = 0; i < lines.length; i++) {
    const p = lineProvenance[i]?.name?.confidence;
    if (p === 'high') high += 1;
    else if (p === 'medium') med += 1;
    else low += 1;
  }

  return {
    lines,
    lineProvenance,
    warnings,
    summary: {
      lineCount: lines.length,
      highConfidenceLines: high,
      mediumConfidenceLines: med,
      lowConfidenceLines: low,
    },
  };
}
